import { BuildCircleOutlined, Edit, ErrorOutlined, InfoOutlined, NewReleasesOutlined } from '@mui/icons-material';
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineSeparator,
    timelineItemClasses,
} from '@mui/lab';
import { Chip, IconButton, Stack, styled, Typography } from '@mui/material';
import { FC } from 'react';
import { announcementColor } from 'src/components/molecules/StatusWidget/StatusWidget';
import { AnnouncementRow } from 'src/domain/table/announcement.row';
import { useIsAdmin } from 'src/hooks/auth/useIsAdmin';
import { AnnouncementTypeEnum } from 'src/infrastructure/rest-api/api-types';

interface Props {
    announcements: AnnouncementRow[];
    handleEditAnnouncement: (announcement: AnnouncementRow) => void;
}

export const AnnouncementTimeline: FC<Props> = ({ announcements, handleEditAnnouncement }) => {
    const isAdmin = useIsAdmin();

    const announcementIsResolved = (announcement: AnnouncementRow) => {
        return (
            (announcement.type === 'MajorIncident' || announcement.type === 'MinorIncident') && announcement.resolved
        );
    };

    return (
        <Timeline
            sx={{
                [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                },
                padding: 0,
            }}
        >
            {announcements.map((announcement, index) => (
                <TimelineItem key={announcement.created?.toString()}>
                    <TimelineSeparator>
                        <TimelineItemDot announcementType={announcement.type}>
                            {announcement.type === 'Information' && <InfoOutlined />}
                            {announcement.type === 'Maintenance' && <BuildCircleOutlined />}
                            {announcement.type === 'MajorIncident' && <NewReleasesOutlined />}
                            {announcement.type === 'MinorIncident' && <ErrorOutlined />}
                        </TimelineItemDot>
                        {announcements.length - 1 !== index && (
                            <TimelineConnector sx={{ backgroundColor: '#D5D6DC' }} />
                        )}
                    </TimelineSeparator>
                    <TimelineContent sx={{ marginTop: 2 }}>
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <AnnouncementTitle variant="subtitle1" type={announcement.type}>
                                {announcement.title}
                            </AnnouncementTitle>

                            {announcementIsResolved(announcement) && (
                                <Chip color="success" variant="filled" label="Resolved" />
                            )}
                            {isAdmin && (
                                <IconButton
                                    color="primary"
                                    onClick={() => {
                                        handleEditAnnouncement({
                                            id: announcement.id,
                                            title: announcement.title,
                                            description: announcement.description,
                                            type: announcement.type,
                                            module: announcement.module,
                                            created: announcement.created,
                                            modified: announcement.modified,
                                            resolved: announcement.resolved,
                                            read: announcement.read,
                                        });
                                    }}
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>

                        {announcement.modified && (
                            <Typography
                                variant="caption"
                                color="primary"
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                {announcement.modified &&
                                    !announcement.resolved &&
                                    `Updated ${announcement.modified.toLocaleTimeString([], {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}`}
                            </Typography>
                        )}
                        <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                                fontWeight: 'bold',
                            }}
                        >
                            {announcement.modified && !announcement.resolved && 'Created '}
                            {announcement.created.toLocaleTimeString([], {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                            {announcement.resolved &&
                                ` - ${announcement.resolved.toLocaleTimeString([], {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}`}
                        </Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{announcement.description}</Typography>
                    </TimelineContent>
                </TimelineItem>
            ))}
        </Timeline>
    );
};

export const TimelineItemDot = styled(TimelineDot, {
    shouldForwardProp: (prop) => prop !== 'announcementType',
})<{ announcementType: AnnouncementTypeEnum }>(({ theme, announcementType }) => ({
    backgroundColor: announcementColor(announcementType, theme),
    color: 'white',
    boxShadow: 'none',
}));

export const AnnouncementTitle = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'type',
})<{ type?: AnnouncementTypeEnum }>(({ type, theme }) => ({
    whiteSpace: 'pre-wrap',
    color: announcementColor(type ?? 'Information', theme),
}));
